import {unzip, Unzipped, zip} from "fflate";

// The API runs on Heroku, which drops a request that takes more than 30 s, and uploading counts toward that.
// Big exports (a 16 MB Telegram result.json, a WhatsApp zip with photos) took most of that just to upload,
// so the chat file is shrunk before it's sent: only the text files of an export are kept, zipped.
// The server already reads zips of both exports, so nothing changes there.

// Smaller files upload fast enough as they are
const MIN_SIZE_TO_COMPACT = 1_000_000;
const TEXT_ENTRY = /\.(txt|json|html?)$/i;

const unzipAsync = (data: Uint8Array, filter: (file: {name: string}) => boolean) =>
    new Promise<Unzipped>((resolve, reject) =>
        unzip(data, {filter}, (err, files) => (err ? reject(err) : resolve(files))));

const zipAsync = (files: Unzipped) =>
    new Promise<Uint8Array>((resolve, reject) =>
        zip(files, {level: 6}, (err, data) => (err ? reject(err) : resolve(data))));

const isZip = (data: Uint8Array) => data.length > 3 && data[0] === 0x50 && data[1] === 0x4b && data[2] === 0x03 && data[3] === 0x04;

// Name a single file gets inside the zip, so the server finds it whatever the user's copy was called
// ("result (1).json" etc.)
const entryName = (data: Uint8Array, chatType: string) => {
    if (chatType === 'Whatsapp') return '_chat.txt';
    const start = new TextDecoder().decode(data.subarray(0, 64)).replace(/^﻿/, '').trimStart();
    return start.startsWith('{') ? 'result.json' : 'messages.html';
};

const compact = async (file: File, chatType: string): Promise<File> => {
    if (file.size < MIN_SIZE_TO_COMPACT) return file;
    try {
        const data = new Uint8Array(await file.arrayBuffer());
        const entries = isZip(data)
            ? await unzipAsync(data, (entry) => TEXT_ENTRY.test(entry.name) && !entry.name.startsWith('__MACOSX'))
            : {[entryName(data, chatType)]: data};
        if (!Object.keys(entries).length) return file;
        const zipped = await zipAsync(entries);
        if (zipped.length >= file.size) return file;
        const name = /\.zip$/i.test(file.name) ? file.name : `${file.name}.zip`;
        return new File([zipped as BlobPart], name, {type: 'application/zip'});
    } catch (err) {
        // The server can still try the original
        console.warn("Couldn't compact the chat file:", err);
        return file;
    }
};

// Both the participants step and chat creation upload the same file, so it's shrunk once
const compacted = new WeakMap<File, Promise<File>>();

export const compactChatFile = (file: File, chatType: string) => {
    let result = compacted.get(file);
    if (!result) {
        result = compact(file, chatType);
        compacted.set(file, result);
    }
    return result;
};
